import type { ReactElement, ReactNode } from "react";

import { Card, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { cn } from "@/lib/utils";

export const authPanelCardClassName = "shadow-xl shadow-primary/10";

type AuthPanelProps = {
  children: ReactNode;
  className?: string;
  width?: "sm" | "md" | "lg";
};

/**
 * Solid card over the auth backdrop — same surface as login / register.
 */
export function AuthPanel({ children, className, width = "md" }: AuthPanelProps): ReactElement {
  return (
    <div
      className={cn(
        "w-full mx-auto",
        width === "sm" && "max-w-sm",
        width === "md" && "max-w-md",
        width === "lg" && "max-w-lg",
      )}
    >
      <Card className={cn(authPanelCardClassName, className)}>{children}</Card>
    </div>
  );
}

type AuthPanelHeaderProps = {
  icon: ReactNode;
  eyebrow?: ReactNode;
  meta?: ReactNode;
  title: ReactNode;
  description?: ReactNode;
};

/** Centered icon + title block used on invite, onboarding, 404, and similar cards. */
export function AuthPanelHeader({
  icon,
  eyebrow,
  meta,
  title,
  description,
}: AuthPanelHeaderProps): ReactElement {
  return (
    <CardHeader className="justify-items-center text-center">
      <div
        className="flex size-12 items-center justify-center rounded-2xl bg-primary/10"
        aria-hidden
      >
        {icon}
      </div>
      {eyebrow ? <p className="text-sm font-semibold text-primary">{eyebrow}</p> : null}
      {meta ? <p className="text-xs font-medium text-muted-foreground">{meta}</p> : null}
      <CardTitle className="text-xl">{title}</CardTitle>
      {description ? <CardDescription>{description}</CardDescription> : null}
    </CardHeader>
  );
}
